import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Ticket, Calendar, MapPin, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/apiUrl';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function MyTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/tickets/my-tickets`);
        setTickets(response.data);
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, []);

  const upcomingTickets = tickets.filter(t => new Date(t.event_date) >= new Date());
  const pastTickets = tickets.filter(t => new Date(t.event_date) < new Date());

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const TicketCard = ({ ticket }) => (
    <Link to={`/dashboard/tickets/${ticket.ticket_id}`} key={ticket.ticket_id}>
      <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-colors cursor-pointer">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="w-20 h-20 rounded-lg bg-muted overflow-hidden flex-shrink-0">
              {ticket.event_image ? (
                <img 
                  src={getImageUrl(ticket.event_image)} 
                  alt={ticket.event_title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Ticket className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate">{ticket.event_title}</h3>
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>{ticket.event_date ? format(new Date(ticket.event_date), 'MMM d, yyyy') : 'TBD'}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs">
                  {ticket.ticket_type_name}
                </Badge>
                <Badge 
                  className={`text-xs ${
                    ticket.status === 'confirmed' 
                      ? 'bg-green-500/10 text-green-500 border-green-500/20' 
                      : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                  }`}
                >
                  {ticket.status}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-primary/10">
            <Ticket className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Tickets</h1>
            <p className="text-muted-foreground">View and manage your event tickets</p>
          </div>
        </div>

        {tickets.length === 0 ? (
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-8 text-center">
              <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No tickets yet</h3>
              <p className="text-muted-foreground mb-4">
                You haven't purchased any tickets yet. Browse events to find something exciting!
              </p>
              <Link 
                to="/events" 
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Browse Events
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="upcoming">
                Upcoming ({upcomingTickets.length})
              </TabsTrigger>
              <TabsTrigger value="past">
                Past ({pastTickets.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="upcoming" className="space-y-4">
              {upcomingTickets.length === 0 ? (
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    No upcoming events
                  </CardContent>
                </Card>
              ) : (
                upcomingTickets.map(ticket => <TicketCard key={ticket.ticket_id} ticket={ticket} />)
              )}
            </TabsContent>
            
            <TabsContent value="past" className="space-y-4">
              {pastTickets.length === 0 ? (
                <Card className="bg-card/50 border-border/50">
                  <CardContent className="p-6 text-center text-muted-foreground">
                    No past events
                  </CardContent>
                </Card>
              ) : (
                pastTickets.map(ticket => <TicketCard key={ticket.ticket_id} ticket={ticket} />)
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
}
